import type { CSSProperties } from "react";
import type { Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import type { AppReportComponentStyles } from "../types";
import { buildReportChatDataTableCustomStyles } from "./dataTable";

export function buildReportComponentStyles(theme: Theme): AppReportComponentStyles {
  return {
    chartErrorText: {
      whiteSpace: "pre-wrap",
      color: "warning.main",
      fontWeight: 500,
    },
    clarificationTitle: { fontWeight: 600, mb: 1.5 },
    clarificationChipRow: { mb: 1.5 },
    clarificationProgress: { display: "block", mb: 1 },
    clarificationQuestion: { mb: 0.75, fontWeight: 500 },
    clarificationOtherField: {
      minWidth: { xs: "100%", sm: 240 },
      flex: { xs: "1 1 100%", sm: "1 1 auto" },
    },
    clarificationSubmitBox: { pt: 0.5 },
    widgetDraftInvalidAlert: { mt: 0.5 },
    widgetDraftPanel: {
      mt: 0.5,
      p: 1,
      borderRadius: 1,
      border: 1,
      borderColor: "divider",
      bgcolor: "action.hover",
    },
    widgetDraftTitle: { fontWeight: 700, mb: 1 },
    widgetDraftMessage: { mb: 1.5, color: "text.secondary" },
    widgetDraftFormGrid: { mb: 1 },
    widgetDraftTextButton: { textTransform: "none" },
    widgetDraftEditorBox: { mb: 1 },
    widgetDraftAlert: { mb: 1 },
    widgetDraftQuestion: { mb: 1, fontWeight: 600 },
    widgetDraftActions: { mt: 1 },
    quotaTitle: {
      color: "warning.main",
      fontWeight: 700,
      whiteSpace: "pre-wrap",
    },
    quotaDetail: { mt: 0.5, color: "warning.main" },
    errorText: {
      color: "error.main",
      whiteSpace: "pre-wrap",
      fontFamily: "inherit",
    },
    errorAlert: { mt: 0.5 },
    errorAlertTitle: { fontWeight: 600 },
    errorAlertCaption: { mt: 0.5, display: "block", opacity: 0.9 },
    messageTitle: {
      fontWeight: 600,
      mb: 0.5,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    },
    messageBody: {
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    },
    messageTitleCompact: {
      fontWeight: 600,
      mb: 1,
      whiteSpace: "pre-wrap",
    },
    messageTitleTable: {
      fontWeight: 600,
      mb: 1,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      flexShrink: 0,
    },
    fitContainerBox: (fitContainer) =>
      fitContainer
        ? {
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            height: "100%",
          }
        : {},
    tableHtmlBox: (fitContainer, darkMode) => ({
      overflow: "auto",
      maxHeight: fitContainer ? "100%" : 360,
      flex: fitContainer ? 1 : undefined,
      minHeight: fitContainer ? 0 : undefined,
      "& table": {
        borderCollapse: "collapse",
        tableLayout: "fixed",
        width: "100%",
        fontSize: "0.875rem",
      },
      "& th, & td": {
        border: `1px solid ${darkMode ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.12)"}`,
        padding: "8px 12px",
        textAlign: "left",
      },
      "& th": {
        fontWeight: 600,
        backgroundColor: darkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)",
      },
    }),
    chartWarning: { color: "warning.main", fontWeight: 500 },
    chartOuterBox: (fitContainer) =>
      fitContainer
        ? {
            height: "100%",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            flex: 1,
          }
        : {},
    chartMessageTitle: (fitContainer) => ({
      fontWeight: 600,
      mb: 0.5,
      flexShrink: 0,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      fontSize: fitContainer ? "0.8rem" : undefined,
    }),
    chartInnerBox: (fitContainer, chartHeight, chartMinWidth) => ({
      height: fitContainer ? "100%" : chartHeight,
      width: "100%",
      minWidth: fitContainer ? 0 : chartMinWidth,
      minHeight: fitContainer ? 0 : undefined,
      flex: fitContainer ? 1 : undefined,
      flexShrink: fitContainer ? undefined : 0,
      overflow: "hidden",
      "& > div": { width: "100% !important", height: "100% !important" },
    }),
    chartEchartsStyle: (fitContainer, chartHeight, chartMinWidth): CSSProperties =>
      fitContainer
        ? { height: "100%", width: "100%" }
        : { height: `${chartHeight}px`, width: "100%", minWidth: `${chartMinWidth}px` },
    jsonPre: (darkMode) => ({
      whiteSpace: "pre-wrap",
      fontFamily: "monospace",
      fontSize: "0.875rem",
      overflow: "auto",
      p: 1,
      borderRadius: 1,
      bgcolor: darkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
    }),
    htmlIframe: (darkMode) => ({
      width: "100%",
      minHeight: 480,
      border: "none",
      borderRadius: 1,
      // Match themed srcDoc surface so black AI HTML never sits on a dark iframe chrome.
      bgcolor: darkMode ? "#1a1d23" : "#ffffff",
      colorScheme: darkMode ? "dark" : "light",
    }),
    htmlWarning: { color: "warning.main" },
    defaultPre: {
      whiteSpace: "pre-wrap",
      fontFamily: "monospace",
      fontSize: "0.875rem",
      overflow: "auto",
    },
  };
}

export function buildAuditLogDataTableHeadStyles(theme: Theme) {
  const base = theme.palette.mode;
  void base;
  return {
    fontSize: "0.8rem",
  };
}

export function buildAuditLogDataTableCellStyles() {
  return {
    fontSize: "0.8125rem",
    alignItems: "flex-start" as const,
  };
}

export function buildAuditLogDataTableCustomStyles(theme: Theme) {
  const tableShellStyles = buildReportChatDataTableCustomStyles(theme);
  return {
    ...tableShellStyles,
    headCells: {
      style: {
        ...tableShellStyles.headCells.style,
        ...buildAuditLogDataTableHeadStyles(theme),
      },
    },
    cells: {
      style: {
        ...tableShellStyles.cells.style,
        ...buildAuditLogDataTableCellStyles(),
      },
    },
  };
}
